import React, { useState, useEffect, useCallback } from "react";
import { CompanyAdminLayout } from "../../../components/CompanyAdmin/CompanyAdminLayout";
import { useAuthStore } from "../../../stores/auth-store";
import { companyQuoteInquiryUseCase } from "di/container";
import type {
  QuoteInquiryListItem,
  QuoteInquiryDetail,
  QuoteInquiryStatus,
} from "domain/types/quote-inquiry";
import {
  QuoteInquiryStatusLabels,
  QuoteInquiryStatusColors,
} from "domain/types/quote-inquiry";

type ViewMode = "list" | "detail";

const QuoteInquiryManagement: React.FC = () => {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [inquiries, setInquiries] = useState<QuoteInquiryListItem[]>([]);
  const [selectedInquiry, setSelectedInquiry] =
    useState<QuoteInquiryDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 필터
  const [statusFilter, setStatusFilter] = useState<QuoteInquiryStatus | "">("");
  const [keyword, setKeyword] = useState("");

  // 페이징
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // 답변 폼
  const [replySubject, setReplySubject] = useState("");
  const [replyContent, setReplyContent] = useState("");
  const [isReplying, setIsReplying] = useState(false);

  const fetchInquiries = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setError(null);
    try {
      const result = await companyQuoteInquiryUseCase.getQuoteInquiries({
        companyId,
        status: statusFilter || undefined,
        keyword: keyword || undefined,
        page,
        size: 20,
      });

      setInquiries(result?.content || []);
      setTotalPages(result?.totalPages || 0);
      setTotalElements(result?.totalElements || 0);
    } catch (err: any) {
      setError(err.message || "목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  }, [companyId, statusFilter, keyword, page]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const handleViewDetail = async (inquiryId: number) => {
    if (!companyId) return;

    setLoading(true);
    try {
      const detail = await companyQuoteInquiryUseCase.getQuoteInquiry({
        companyId,
        inquiryId,
      });
      setSelectedInquiry(detail);
      setViewMode("detail");
    } catch (err: any) {
      setError(err.message || "상세 정보를 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToList = () => {
    setViewMode("list");
    setSelectedInquiry(null);
    setReplySubject("");
    setReplyContent("");
  };

  const handleReply = async () => {
    if (!companyId || !selectedInquiry) return;
    if (!replySubject.trim() || !replyContent.trim()) {
      alert("답변 제목과 내용을 입력해주세요.");
      return;
    }

    setIsReplying(true);
    try {
      const updated = await companyQuoteInquiryUseCase.replyQuoteInquiry({
        companyId,
        inquiryId: selectedInquiry.id,
        request: {
          replySubject: replySubject.trim(),
          replyContent: replyContent.trim(),
        },
      });
      setSelectedInquiry(updated);
      setReplySubject("");
      setReplyContent("");
      alert("답변이 발송되었습니다.");
      fetchInquiries();
    } catch (err: any) {
      alert(err.message || "답변 발송에 실패했습니다.");
    } finally {
      setIsReplying(false);
    }
  };

  const handleStatusChange = async (newStatus: QuoteInquiryStatus) => {
    if (!companyId || !selectedInquiry) return;

    try {
      const updated = await companyQuoteInquiryUseCase.updateQuoteInquiryStatus(
        {
          companyId,
          inquiryId: selectedInquiry.id,
          status: newStatus,
        }
      );
      setSelectedInquiry(updated);
      fetchInquiries();
    } catch (err: any) {
      alert(err.message || "상태 변경에 실패했습니다.");
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderStatusBadge = (status: QuoteInquiryStatus) => {
    const colors = QuoteInquiryStatusColors[status];
    return (
      <span
        className="px-2 py-1 rounded text-xs font-medium"
        style={{ backgroundColor: colors.bg, color: colors.text }}
      >
        {QuoteInquiryStatusLabels[status]}
      </span>
    );
  };

  const renderList = () => (
    <div className="p-6">
      {/* 필터 영역 */}
      <div className="mb-6 flex flex-wrap gap-4 items-center">
        <select
          className="px-3 py-2 border border-[#e0e0e0] rounded-lg text-sm"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as QuoteInquiryStatus | "");
            setPage(0);
          }}
        >
          <option value="">전체 상태</option>
          <option value="PENDING">대기중</option>
          <option value="REPLIED">답변완료</option>
          <option value="CLOSED">종료</option>
        </select>

        <div className="flex gap-2">
          <input
            type="text"
            className="px-3 py-2 border border-[#e0e0e0] rounded-lg text-sm w-64"
            placeholder="문의자명, 이메일, 상품명 검색"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                setPage(0);
                fetchInquiries();
              }
            }}
          />
          <button
            className="px-4 py-2 bg-[#222] text-white rounded-lg text-sm hover:bg-[#333]"
            onClick={() => {
              setPage(0);
              fetchInquiries();
            }}
          >
            검색
          </button>
        </div>
      </div>

      {/* 총 건수 */}
      <div className="mb-4 text-sm text-[#555]">
        총 <span className="font-medium text-[#222]">{totalElements}</span>건
      </div>

      {/* 테이블 */}
      <div className="bg-white border border-[#e0e0e0] rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#fafafa]">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-[#555]">
                상품명
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-[#555]">
                문의자
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-[#555]">
                수량
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-[#555]">
                상태
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-[#555]">
                문의일
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-[#555]">
                답변일
              </th>
              <th className="px-4 py-3 text-center text-sm font-medium text-[#555]">
                관리
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-[#888]">
                  불러오는 중...
                </td>
              </tr>
            ) : inquiries.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-[#888]">
                  견적 문의가 없습니다.
                </td>
              </tr>
            ) : (
              inquiries.map((inquiry) => (
                <tr
                  key={inquiry.id}
                  className="border-t border-[#eee] hover:bg-[#fafafa]"
                >
                  <td className="px-4 py-3 text-sm text-[#222]">
                    {inquiry.productName || "-"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-[#222]">
                      {inquiry.inquirerName}
                    </div>
                    <div className="text-xs text-[#888]">
                      {inquiry.inquirerEmail}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#222]">
                    {inquiry.quantity}
                  </td>
                  <td className="px-4 py-3">
                    {renderStatusBadge(inquiry.status)}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#555]">
                    {formatDate(inquiry.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#555]">
                    {inquiry.repliedAt ? formatDate(inquiry.repliedAt) : "-"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      className="px-3 py-1 text-sm text-[#4285F4] hover:text-[#3367D6]"
                      onClick={() => handleViewDetail(inquiry.id)}
                    >
                      상세보기
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="mt-4 flex justify-center gap-2">
          <button
            className="px-3 py-1 border border-[#e0e0e0] rounded text-sm disabled:opacity-50"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            이전
          </button>
          <span className="px-3 py-1 text-sm text-[#555]">
            {page + 1} / {totalPages}
          </span>
          <button
            className="px-3 py-1 border border-[#e0e0e0] rounded text-sm disabled:opacity-50"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            다음
          </button>
        </div>
      )}
    </div>
  );

  const renderDetail = () => {
    if (!selectedInquiry) return null;

    return (
      <div className="p-6">
        {/* 뒤로가기 */}
        <button
          className="mb-4 text-sm text-[#555] hover:text-[#222] flex items-center gap-1"
          onClick={handleBackToList}
        >
          ← 목록으로
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 문의 정보 */}
          <div className="bg-white border border-[#e0e0e0] rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-[#222]">문의 정보</h3>
              {renderStatusBadge(selectedInquiry.status)}
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#888] block mb-1">상품명</label>
                <div className="text-sm text-[#222]">
                  {selectedInquiry.productName || "-"}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-[#888] block mb-1">
                    문의자
                  </label>
                  <div className="text-sm text-[#222]">
                    {selectedInquiry.inquirerName}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#888] block mb-1">
                    이메일
                  </label>
                  <div className="text-sm text-[#222]">
                    {selectedInquiry.inquirerEmail}
                  </div>
                </div>
              </div>

              {selectedInquiry.inquirerMobile && (
                <div>
                  <label className="text-xs text-[#888] block mb-1">
                    연락처
                  </label>
                  <div className="text-sm text-[#222]">
                    {selectedInquiry.inquirerMobile}
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs text-[#888] block mb-1">수량</label>
                <div className="text-sm text-[#222]">
                  {selectedInquiry.quantity}
                </div>
              </div>

              {selectedInquiry.productOptionsSnapshot && (
                <div>
                  <label className="text-xs text-[#888] block mb-1">
                    선택 옵션
                  </label>
                  <div className="text-sm text-[#222] bg-[#f5f5f5] p-3 rounded">
                    {selectedInquiry.productOptionsSnapshot}
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs text-[#888] block mb-1">
                  문의 내용
                </label>
                <div className="text-sm text-[#222] bg-[#f5f5f5] p-3 rounded whitespace-pre-wrap">
                  {selectedInquiry.inquiryContent}
                </div>
              </div>

              <div>
                <label className="text-xs text-[#888] block mb-1">
                  문의일시
                </label>
                <div className="text-sm text-[#555]">
                  {formatDate(selectedInquiry.createdAt)}
                </div>
              </div>
            </div>

            {/* 상태 변경 버튼 */}
            {selectedInquiry.status !== "CLOSED" && (
              <div className="mt-6 pt-4 border-t border-[#eee]">
                <button
                  className="px-4 py-2 text-sm text-[#555] border border-[#e0e0e0] rounded hover:bg-[#f5f5f5]"
                  onClick={() => handleStatusChange("CLOSED")}
                >
                  문의 종료
                </button>
              </div>
            )}
          </div>

          {/* 답변 영역 */}
          <div className="bg-white border border-[#e0e0e0] rounded-lg p-6">
            <h3 className="text-lg font-medium text-[#222] mb-4">답변</h3>

            {selectedInquiry.status === "REPLIED" ||
            selectedInquiry.status === "CLOSED" ? (
              // 답변 완료 상태
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-[#888] block mb-1">
                    답변 제목
                  </label>
                  <div className="text-sm text-[#222]">
                    {selectedInquiry.replySubject}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#888] block mb-1">
                    답변 내용
                  </label>
                  <div className="text-sm text-[#222] bg-[#f5f5f5] p-3 rounded whitespace-pre-wrap">
                    {selectedInquiry.replyContent}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-[#888] block mb-1">
                      답변자
                    </label>
                    <div className="text-sm text-[#555]">
                      {selectedInquiry.repliedByName || "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-[#888] block mb-1">
                      답변일시
                    </label>
                    <div className="text-sm text-[#555]">
                      {selectedInquiry.repliedAt
                        ? formatDate(selectedInquiry.repliedAt)
                        : "-"}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // 답변 작성 폼
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-[#888] block mb-1">
                    답변 제목 *
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-[#e0e0e0] rounded-lg text-sm focus:border-[#4285F4] focus:outline-none"
                    placeholder="답변 제목을 입력하세요"
                    value={replySubject}
                    onChange={(e) => setReplySubject(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-[#888] block mb-1">
                    답변 내용 *
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-[#e0e0e0] rounded-lg text-sm focus:border-[#4285F4] focus:outline-none resize-none"
                    rows={8}
                    placeholder="답변 내용을 입력하세요"
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    className="px-6 py-2 bg-[#222] text-white rounded-lg text-sm hover:bg-[#333] disabled:opacity-50"
                    onClick={handleReply}
                    disabled={isReplying}
                  >
                    {isReplying ? "발송 중..." : "답변 발송"}
                  </button>
                </div>
                <p className="text-xs text-[#888]">
                  * 답변 발송 시 문의자 이메일로 답변 내용이 발송됩니다.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <CompanyAdminLayout>
      <div className="min-h-screen bg-[#f5f5f5]">
        {/* 헤더 */}
        <div className="bg-white border-b border-[#e0e0e0] px-6 py-4">
          <h1 className="text-xl font-bold text-[#222]">견적 문의 관리</h1>
          <p className="text-sm text-[#888] mt-1">
            고객의 견적 문의를 확인하고 답변을 발송할 수 있습니다.
          </p>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-[#FEF2F2] text-[#F24A4A] text-sm rounded-lg">
            {error}
          </div>
        )}

        {/* 메인 콘텐츠 */}
        {viewMode === "list" ? renderList() : renderDetail()}
      </div>
    </CompanyAdminLayout>
  );
};

export default QuoteInquiryManagement;
